import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Table } from 'primeng/table';
import { ConfirmationService } from 'primeng/api';
import { FileSaverService } from 'ngx-filesaver';
import { DividendoService } from '../../../services/dividendo.service';
import { DividendoResponse, ListarDividendoRequest } from '../../../models/dividendo.model';
import {
  FormaDistribuicaoDividendoEnum,
  StatusReinfDividendoEnum
} from '../../../models/enums/dividendo.enum';
import { SpinnerService } from '../../../services/spinner.service';
import { ToastService } from '../../../services/toast.service';
import { SeveridadeToast } from '../../../models/enums/severidade-toast.enum';
import { JwtHelper } from '../../../helpers/jwtDecode.helper';
import { SociosService } from '../../../services/socios.service';
import { SocioResponse } from '../../../models/socioResponse.model';
import { retornaDescricao } from '../../../shared/mock/descricao';

@Component({
  selector: 'app-dividendos-listagem',
  templateUrl: './dividendos-listagem.component.html',
  styleUrls: ['./dividendos-listagem.component.scss'],
  providers: [ConfirmationService]
})
export class DividendosListagemComponent implements OnInit, OnDestroy {
  @Output() tituloChange = new EventEmitter<string>();
  @Output() iconeChange = new EventEmitter<string>();
  @Output() tituloDescricao = new EventEmitter<string>();
  @Output() textoDescricao = new EventEmitter<string>();
  @Output() mostrarDescricao = new EventEmitter<boolean>();

  @ViewChild('dt') dt?: Table;

  dividendos: DividendoResponse[] = [];
  socios: SocioResponse[] = [];
  usuarioId = '';
  isLoading = false;
  exibirDetalhes = false;
  dividendoSelecionado?: DividendoResponse;

  filtroDataInicio?: Date;
  filtroDataFim?: Date;
  filtroFormaDistribuicao?: FormaDistribuicaoDividendoEnum;
  filtroSocioId?: number;
  filtroStatusReinf?: StatusReinfDividendoEnum;

  formasDistribuicao = [
    { label: 'Distribuição Regular', value: FormaDistribuicaoDividendoEnum.Regular },
    { label: 'Antecipação de Dividendos', value: FormaDistribuicaoDividendoEnum.Antecipacao }
  ];

  statusReinfOptions = [
    { label: 'Não enviado', value: StatusReinfDividendoEnum.NaoEnviado },
    { label: 'Enviado ao REINF', value: StatusReinfDividendoEnum.Enviado },
    { label: 'Retificado', value: StatusReinfDividendoEnum.Retificado },
    { label: 'Erro', value: StatusReinfDividendoEnum.Erro },
    { label: 'Excluído', value: StatusReinfDividendoEnum.Excluido }
  ];

  constructor(
    private router: Router,
    private dividendoService: DividendoService,
    private sociosService: SociosService,
    private spinnerService: SpinnerService,
    private toastService: ToastService,
    private confirmationService: ConfirmationService,
    private fileSaverService: FileSaverService,
    private jwtHelper: JwtHelper
  ) { }

  ngOnInit(): void {
    this.tituloChange.emit('Distribuição de Dividendos');
    this.iconeChange.emit('pi pi-chart-line');
    this.tituloDescricao.emit(retornaDescricao('dividendos')?.titulo ?? 'Distribuição de Dividendos');
    this.textoDescricao.emit(retornaDescricao('dividendos')?.texto ?? 'Cadastre e acompanhe as distribuições de dividendos aos sócios.');
    this.mostrarDescricao.emit(true);

    this.usuarioId = this.jwtHelper.getUserIdFromToken();
    this.carregarSocios();
    this.carregarDividendos();
  }

  ngOnDestroy(): void {
    this.mostrarDescricao.emit(false);
  }

  carregarSocios(): void {
    if (!this.usuarioId) return;

    this.sociosService.listarSocios(this.usuarioId).subscribe({
      next: (socios) => this.socios = socios,
      error: () => this.socios = []
    });
  }

  carregarDividendos(): void {
    if (!this.usuarioId) return;

    this.isLoading = true;
    this.spinnerService.show('Carregando dividendos...');

    this.dividendoService.listar(this.montarFiltro()).subscribe({
      next: (dividendos) => {
        this.dividendos = dividendos;
        this.isLoading = false;
        this.spinnerService.hide();
      },
      error: (error) => {
        this.isLoading = false;
        this.spinnerService.hide();
        this.toastService.setMensagemToast(
          `Erro ao carregar dividendos: ${error.error ?? error.message}`,
          SeveridadeToast.Error
        );
      }
    });
  }

  montarFiltro(ordenarPor?: string): ListarDividendoRequest {
    return {
      usuarioId: this.usuarioId,
      dataInicio: this.filtroDataInicio ? this.formatarData(this.filtroDataInicio) : undefined,
      dataFim: this.filtroDataFim ? this.formatarData(this.filtroDataFim) : undefined,
      formaDistribuicao: this.filtroFormaDistribuicao,
      socioId: this.filtroSocioId,
      statusReinf: this.filtroStatusReinf,
      ordenarPor: ordenarPor,
      ordemDecrescente: true
    };
  }

  aplicarFiltros(): void {
    this.carregarDividendos();
  }

  limparFiltros(): void {
    this.filtroDataInicio = undefined;
    this.filtroDataFim = undefined;
    this.filtroFormaDistribuicao = undefined;
    this.filtroSocioId = undefined;
    this.filtroStatusReinf = undefined;
    this.carregarDividendos();
  }

  applyFilterGlobal(value: string): void {
    this.dt?.filterGlobal(value, 'contains');
  }

  openNew(): void {
    this.router.navigate(['/dividendos/adicionar']);
  }

  verDetalhes(dividendo: DividendoResponse): void {
    this.spinnerService.show('Carregando detalhes...');
    this.dividendoService.obter(dividendo.id, this.usuarioId).subscribe({
      next: (detalhe) => {
        this.dividendoSelecionado = detalhe;
        this.exibirDetalhes = true;
        this.spinnerService.hide();
      },
      error: (error) => {
        this.spinnerService.hide();
        this.toastService.setMensagemToast(
          error?.error?.message ?? 'Não foi possível carregar os detalhes do dividendo.',
          SeveridadeToast.Error
        );
      }
    });
  }

  retificar(dividendo: DividendoResponse): void {
    this.router.navigate(['/dividendos/retificar', dividendo.id]);
  }

  cancelar(dividendo: DividendoResponse): void {
    this.confirmationService.confirm({
      message: 'Deseja cancelar esta distribuição de dividendos?',
      header: 'Confirmar cancelamento',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.spinnerService.show('Cancelando dividendo...');
        this.dividendoService.cancelar(dividendo.id, this.usuarioId).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toastService.setMensagemToast('Dividendo cancelado com sucesso.', SeveridadeToast.Success);
            this.carregarDividendos();
          },
          error: (error) => {
            this.spinnerService.hide();
            this.toastService.setMensagemToast(error.error ?? 'Erro ao cancelar dividendo.', SeveridadeToast.Error);
          }
        });
      }
    });
  }

  exportarExcel(): void {
    this.spinnerService.show('Gerando Excel...');
    this.dividendoService.exportarExcel(this.montarFiltro()).subscribe({
      next: (blob) => {
        this.fileSaverService.save(blob, 'dividendos.xlsx');
        this.spinnerService.hide();
      },
      error: (error) => {
        this.spinnerService.hide();
        this.toastService.setMensagemToast('Erro ao exportar Excel.', SeveridadeToast.Error);
      }
    });
  }

  getStatusSeverity(status: string): 'success' | 'info' | 'warning' | 'danger' | 'secondary' | 'contrast' | undefined {
    switch (status) {
      case 'Pago': return 'success';
      case 'Pendente': return 'warning';
      case 'Cancelado': return 'danger';
      default: return 'info';
    }
  }

  private formatarData(data: Date): string {
    return data.toISOString().split('T')[0];
  }
}
